<template>
  <div class="followed-companies-page">
    <div class="page-toolbar">
      <h4 class="mb-0 font-weight-bolder">
        Followed Brands
      </h4>
      <b-form-input
        v-model="search"
        placeholder="Search"
        class="search-input"
        @input="debouncedSearch"
      />
    </div>

    <div
      v-if="isLoading"
      class="state-panel"
    >
      <b-spinner label="Loading..." />
    </div>

    <div
      v-else-if="loadError"
      class="state-panel text-danger"
    >
      <p>{{ loadError }}</p>
      <b-button
        size="sm"
        variant="primary"
        @click="fetchList"
      >
        Retry
      </b-button>
    </div>

    <div
      v-else-if="!rows.length"
      class="state-panel"
    >
      <p>This user is not following any brands yet.</p>
    </div>

    <b-card
      v-else
      no-body
      class="table-card"
    >
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>BRAND</th>
              <th>FOLLOWED ON</th>
              <th class="text-center">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.id"
            >
              <td>
                <div class="cell-media">
                  <b-avatar
                    :src="row.logo"
                    size="32"
                  >
                    {{ row.initials }}
                  </b-avatar>
                  <span>{{ row.name }}</span>
                </div>
              </td>
              <td>{{ row.followedOn }}</td>
              <td class="text-center">
                <b-button
                  size="sm"
                  variant="outline-danger"
                  :disabled="removingId === row.advertiserId"
                  @click="unfollow(row)"
                >
                  Unfollow
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <AdminPaginationBar
        :page="currentPage"
        :per-page="perPage"
        :total="totalRows"
        @change="onPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BAvatar,
  BButton,
  BCard,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import { applyListPagination, debounce, unwrapAdminResponse } from '@/utils/adminApiHelpers'

export default {
  name: 'FollowedCompanies',
  components: {
    BAvatar,
    BButton,
    BCard,
    BFormInput,
    BSpinner,
    AdminPaginationBar,
  },
  data() {
    return {
      search: '',
      isLoading: false,
      loadError: '',
      rows: [],
      removingId: null,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
    }
  },
  computed: {
    userId() {
      return this.$route.params.id
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.reloadPageOne()
    }, 400)
    this.fetchList()
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page
      this.fetchList()
    },
    reloadPageOne() {
      this.currentPage = 1
      this.fetchList()
    },
    mapRow(item) {
      const name = item.name || item.company_name || item.advertiser_name || 'Brand'
      return {
        id: item.brand_id || item.id,
        advertiserId: item.brand_id || item.advertiser_id || item.id,
        name,
        initials: name.slice(0, 2).toUpperCase(),
        logo: item.logo || item.image || '',
        followedOn: item.followed_at || item.created_at || '—',
      }
    },
    async fetchList() {
      if (!this.userId) {
        this.loadError = 'User id is required.'
        return
      }
      this.isLoading = true
      this.loadError = ''
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
        }
        const q = this.search.trim()
        if (q) params.search = q

        const res = await adminPanelApi.followedCompanies(this.userId, params)
        const payload = unwrapAdminResponse(res)
        const list = payload.companies || payload.items || []
        applyListPagination(this, res, payload)
        this.rows = (Array.isArray(list) ? list : []).map(item => this.mapRow(item))
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load followed brands.'
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
    async unfollow(row) {
      this.removingId = row.advertiserId
      try {
        await adminPanelApi.unfollowCompany(this.userId, row.advertiserId)
        this.rows = this.rows.filter(r => r.advertiserId !== row.advertiserId)
      } catch (e) {
        this.$swal({
          title: 'Error',
          text: e.response?.data?.message || 'Unable to unfollow.',
          icon: 'error',
        })
      } finally {
        this.removingId = null
      }
    },
  },
}
</script>

<style scoped lang="scss">
.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.search-input {
  max-width: 240px;
}

.state-panel {
  text-align: center;
  padding: 2.5rem 1rem;
}

.table-card {
  border: 1px solid #ebe9f1;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 0.85rem 1rem;
  border-top: 1px solid #ebe9f1;
}

.data-table th {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #6e6b7b;
}

.cell-media {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
